import type {
    FocusEventHandler,
    KeyboardEventHandler,
    MouseEventHandler,
    PointerEventHandler,
    ReactNode,
} from 'react';

import type { TooltipPlacement } from '@/components/atoms/Tooltip';

export type ButtonVariant =
    | 'primary'
    | 'success'
    | 'secondary'
    | 'tertiary'
    | 'danger'
    /** Filled red, for the point of no return: an emergency stop, a delete's last step. */
    | 'dangerSolid'
    | 'ghost'
    /** A chip on a photo, such as the image viewer's zoom controls. */
    | 'overlay'
    /** The image viewer toolbar's toggles and panel triggers, on its black bar. */
    | 'toolbar'
    | 'link'
    | 'table';

export type ButtonSize = 'small' | 'medium' | 'large';

export interface ButtonBaseProps {
    variant?: ButtonVariant;
    size?: ButtonSize;
    /** Drawn before the label. */
    icon?: ReactNode;
    /** Drawn after the label, e.g. a caret. */
    iconEnd?: ReactNode;
    children?: ReactNode;
    fullWidth?: boolean;
    /** Lets a long label wrap onto more lines, growing the button, instead of running past its box. */
    wrap?: boolean;
    className?: string;
    id?: string;
    name?: string;
    title?: string;
    tabIndex?: number;
    disabled?: boolean;
    tooltip?: ReactNode;
    tooltipPlacement?: TooltipPlacement;
    /**
     * Analytics id. Without it the tooltip text, then `name`, is used, and nothing is sent if
     * neither is set. Links send nothing; their navigation is counted as a page view.
     */
    trackingId?: string;
    'aria-describedby'?: string;
    'aria-controls'?: string;
    'aria-expanded'?: boolean;
    'aria-haspopup'?: boolean | 'menu' | 'listbox' | 'dialog';
    'aria-current'?: boolean | 'page' | 'step' | 'location' | 'true' | 'false';
    'data-testid'?: string;
}

/** An icon-only button has no visible text, so it must carry its own accessible name. */
export type ButtonLabelProps =
    | { iconOnly?: false; 'aria-label'?: string }
    | { iconOnly: true; 'aria-label': string };

export interface NativeButtonProps {
    to?: undefined;
    type?: 'button' | 'submit' | 'reset';
    form?: string;
    value?: string | number;
    /** Renders `aria-pressed`, for buttons that are toggles. */
    pressed?: boolean;
    /** Unavailable but still focusable, e.g. a scroll button at the end of its range. */
    'aria-disabled'?: boolean;
    /** Keeps the click from reaching click handlers on ancestors, e.g. a clickable table row. */
    stopPropagation?: boolean;
    onClick?: MouseEventHandler<HTMLButtonElement>;
    onMouseDown?: MouseEventHandler<HTMLButtonElement>;
    /** A menu trigger opens on pointer down, as native menus do. */
    onPointerDown?: PointerEventHandler<HTMLButtonElement>;
    onMouseEnter?: MouseEventHandler<HTMLButtonElement>;
    onMouseLeave?: MouseEventHandler<HTMLButtonElement>;
    onKeyDown?: KeyboardEventHandler<HTMLButtonElement>;
    onFocus?: FocusEventHandler<HTMLButtonElement>;
    onBlur?: FocusEventHandler<HTMLButtonElement>;
}

export interface LinkButtonProps {
    /** Renders a link styled as a button. Router path, or an absolute URL for an external page. */
    to: string;
    target?: string;
    /** Forces a plain `<a>` and a full page load. */
    external?: boolean;
    onClick?: MouseEventHandler<HTMLAnchorElement>;
    onMouseEnter?: MouseEventHandler<HTMLAnchorElement>;
    onMouseLeave?: MouseEventHandler<HTMLAnchorElement>;
    onFocus?: FocusEventHandler<HTMLAnchorElement>;
    onBlur?: FocusEventHandler<HTMLAnchorElement>;
}

export type ButtonProps = ButtonBaseProps &
    ButtonLabelProps &
    (NativeButtonProps | LinkButtonProps);

export function isLinkProps(
    props: ButtonProps
): props is ButtonBaseProps & ButtonLabelProps & LinkButtonProps {
    return typeof props.to === 'string';
}
